import React from 'react'
import { 
  FaSync, FaCheckCircle, FaExclamationCircle, FaEye, FaTrash, FaEdit,
  FaUser, FaEnvelope, FaPhone, FaCalendar, FaMapMarker, FaMoneyBill, 
  FaBuilding, FaUniversity, FaIdCard, FaClock, FaCheck
} from 'react-icons/fa'
import dayjs from 'dayjs'
import { startCase } from 'lodash'
  const DetailModalContent = ({ data, onClose }) => {
    const { employee, evereeData } = data
    if (!evereeData) return null

    const formatCurrency = (amount) => {
      if (!amount) return 'N/A'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: amount.currency || 'USD'
      }).format(parseFloat(amount.amount) || 0)
    }

    const formatDate = (date) => {
      if (!date) return 'N/A'
      return dayjs(date).format('MMM D, YYYY')
    }

    const getStatusBadge = (status) => {
      const colors = {
        'ACTIVE': 'bg-green-100 text-green-800',
        'ONBOARDING': 'bg-blue-100 text-blue-800',
        'SEPARATED': 'bg-red-100 text-red-800',
        'NEEDS_VERIFICATION': 'bg-yellow-100 text-yellow-800'
      }
      return colors[status] || 'bg-gray-100 text-gray-800'
    }

    return (
      <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
        <div className='bg-white rounded-lg p-6 w-[700px] max-h-[90vh] overflow-y-auto'>
          {/* Header */}
          <div className='flex justify-between items-center mb-4 border-b pb-3'>
            <div>
              <h4 className='font-semibold text-lg'>{employee?.name || 'Employee Detail'}</h4>
              <p className='text-xs text-gray-500'>Employee ID: {employee?.employeeId}</p>
            </div>
            <button 
              type='button' 
              onClick={onClose} 
              className='text-gray-400 hover:text-gray-600 text-2xl leading-none'
            >
              ×
            </button>
          </div>

          <div className='space-y-4'>
            {/* Basic Information */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaUser className='w-3 h-3' />
                  <span>Full Name</span>
                </div>
                <p className='font-medium'>{evereeData.firstName} {evereeData.lastName}</p>
              </div>
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaIdCard className='w-3 h-3' />
                  <span>Worker ID</span>
                </div>
                <p className='font-medium text-sm'>{evereeData.id || 'N/A'}</p>
              </div>
            </div>

            {/* Contact Information */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaEnvelope className='w-3 h-3' />
                  <span>Email</span>
                </div>
                <p className='font-medium text-sm'>{evereeData.email || 'N/A'}</p>
              </div>
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaPhone className='w-3 h-3' />
                  <span>Phone</span>
                </div>
                <p className='font-medium text-sm'>{evereeData.phoneNumber || 'N/A'}</p>
              </div>
            </div>

            {/* Date of Birth & Hire Date */}
            <div className='grid grid-cols-2 gap-4'>
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaCalendar className='w-3 h-3' />
                  <span>Date of Birth</span>
                </div>
                <p className='font-medium'>{formatDate(evereeData.dateOfBirth)}</p>
              </div>
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaClock className='w-3 h-3' />
                  <span>Hire Date</span>
                </div>
                <p className='font-medium'>{formatDate(evereeData.hireDate)}</p>
              </div>
            </div>

            {/* Address */}
            {evereeData.homeAddress && (
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaMapMarker className='w-3 h-3' />
                  <span>Home Address</span>
                </div>
                <p className='font-medium text-sm'>
                  {evereeData.homeAddress.line1}
                  {evereeData.homeAddress.line2 && `, ${evereeData.homeAddress.line2}`}
                  <br />
                  {evereeData.homeAddress.city}, {evereeData.homeAddress.state} {evereeData.homeAddress.postalCode}
                </p>
              </div>
            )}

            {/* Position & Pay */}
            {evereeData.position && (
              <div className='bg-blue-50 p-3 rounded-lg border border-blue-200'>
                <div className='flex items-center gap-2 text-xs text-blue-600 mb-1'>
                  <FaMoneyBill className='w-3 h-3' />
                  <span className='font-semibold'>Position & Pay</span>
                </div>
                <div className='grid grid-cols-2 gap-2'>
                  <div>
                    <p className='text-xs text-gray-500'>Pay Type</p>
                    <p className='font-medium'>{evereeData.position.payType}</p>
                  </div>
                  <div>
                    <p className='text-xs text-gray-500'>Pay Rate</p>
                    <p className='font-medium'>{formatCurrency(evereeData.position.payRate)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tax & Status */}
            <div className='grid grid-cols-2 gap-4'>
              {evereeData.taxpayerIdentifier && (
                <div className='bg-gray-50 p-3 rounded-lg'>
                  <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                    <FaIdCard className='w-3 h-3' />
                    <span>Taxpayer ID</span>
                  </div>
                  <p className='font-medium'>•••-••-{evereeData.taxpayerIdentifier.slice(-4)}</p>
                  {evereeData.tinVerificationStatus && (
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${getStatusBadge(evereeData.tinVerificationStatus)}`}>
                      {startCase(evereeData.tinVerificationStatus)}
                    </span>
                  )}
                </div>
              )}
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaCheck className='w-3 h-3' />
                  <span>Status</span>
                </div>
                {evereeData.lifecycleStatus && (
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusBadge(evereeData.lifecycleStatus)}`}>
                    {startCase(evereeData.lifecycleStatus)}
                  </span>
                )}
              </div>
            </div>

            {/* Bank Accounts */}
            {evereeData.bankAccounts && evereeData.bankAccounts.length > 0 && (
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaUniversity className='w-3 h-3' />
                  <span>Bank Accounts</span>
                </div>
                {evereeData.bankAccounts.map((bank, idx) => (
                  <div key={idx} className='mt-1 text-sm'>
                    <p className='font-medium'>{bank.bankName}</p>
                    <p className='text-xs text-gray-600'>
                      {bank.accountType} •••• {bank.accountNumberLast4}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Work Location */}
            {evereeData.legalWorkAddress && (
              <div className='bg-gray-50 p-3 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-500 mb-1'>
                  <FaBuilding className='w-3 h-3' />
                  <span>Work Location</span>
                </div>
                <p className='font-medium'>{evereeData.legalWorkAddress.name || 'N/A'}</p>
                <p className='text-xs text-gray-600'>Location ID: {evereeData.legalWorkAddress.workLocationId}</p>
                {evereeData.legalWorkAddress.address && (
                  <p className='text-xs text-gray-500 mt-1'>
                    {evereeData.legalWorkAddress.address.displayString || 
                      `${evereeData.legalWorkAddress.address.city}, ${evereeData.legalWorkAddress.address.state}`}
                  </p>
                )}
              </div>
            )}

            {/* Onboarding Status */}
            <div className='bg-green-50 p-3 rounded-lg border border-green-200'>
              <div className='flex items-center gap-2'>
                <FaCheckCircle className='w-4 h-4 text-green-600' />
                <span className='text-sm font-medium text-green-700'>
                  Onboarding Complete: {evereeData.onboardingComplete ? '✅ Yes' : '❌ No'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

export default DetailModalContent